/**
 * The format registry — what makes this an event *format generator* rather than an
 * .ics writer. A UI enumerates `EVENT_FORMATS` and gets download buttons for free;
 * adding a format here lights it up on the web app, the embed, and the native app.
 */

import { toCsv } from './csv';
import { icsFileName, toIcs, type IcsOptions } from './ics';
import { toJcalString } from './jcal';
import { toJsonLdScript } from './jsonld';
import type { EventDraft } from './model';

export type EventFormatId = 'ics' | 'jcal' | 'csv' | 'jsonld';

/** Per-render inputs a generator can't invent for itself (identity, clock). */
export interface FormatContext {
  readonly uid: string;
  /** iCalendar UTC stamp — see `icsTimestamp`. */
  readonly dtstamp: string;
}

export interface EventFormat {
  readonly id: EventFormatId;
  readonly label: string;
  readonly description: string;
  readonly extension: string;
  readonly mimeType: string;
  render(draft: EventDraft, context: FormatContext): string;
}

const baseName = (draft: EventDraft): string => icsFileName(draft).replace(/\.ics$/, '');

export const EVENT_FORMATS: readonly EventFormat[] = [
  {
    id: 'ics',
    label: 'iCalendar (.ics)',
    description: 'Opens in Apple Calendar, Google Calendar, Outlook — the universal event file.',
    extension: 'ics',
    mimeType: 'text/calendar;charset=utf-8',
    render: (draft, context) => toIcs(draft, context satisfies IcsOptions),
  },
  {
    id: 'jcal',
    label: 'jCal (JSON iCalendar)',
    description: 'The same event as JSON (RFC 7265) — full iCalendar semantics, no text parser.',
    // Not `.json`: JSON-LD already claims that, and two downloads called `standup.json` in one
    // folder is worse than an extension a calendar tool can recognise.
    extension: 'jcal',
    mimeType: 'application/calendar+json;charset=utf-8',
    render: (draft, context) => toJcalString(draft, context),
  },
  {
    id: 'csv',
    label: 'CSV (.csv)',
    description: "Google Calendar and Outlook's spreadsheet importer.",
    extension: 'csv',
    mimeType: 'text/csv;charset=utf-8',
    render: (draft) => toCsv([draft]),
  },
  {
    id: 'jsonld',
    label: 'JSON-LD (schema.org)',
    description: 'Embed in a page so search engines and browsers can read the event.',
    extension: 'json',
    mimeType: 'application/ld+json;charset=utf-8',
    render: (draft) => toJsonLdScript(draft),
  },
];

export function eventFormat(id: EventFormatId): EventFormat {
  const format = EVENT_FORMATS.find((candidate) => candidate.id === id);
  if (format === undefined) throw new Error(`Unknown event format: ${id}`);
  return format;
}

export function renderEvent(id: EventFormatId, draft: EventDraft, context: FormatContext): string {
  return eventFormat(id).render(draft, context);
}

export function eventFileName(id: EventFormatId, draft: EventDraft): string {
  return `${baseName(draft)}.${eventFormat(id).extension}`;
}
